<template>
    <div v-show="isVisible">
        <div class="flex items-center gap-2">
            <div
                class="text-sm uppercase"
                :class="
                    showErrors && control.errorMessage
                        ? 'font-semibold text-error'
                        : ''
                "
            >
                {{ control.label }}
            </div>
            <button
                v-if="hasDetailsModal"
                aria-label="More Info"
                @click="$refs.detailsModal.open"
            >
                <Icon
                    name="question-circle"
                    class="text-[14px] hover:text-brew"
                />
            </button>
        </div>

        <div
            v-if="control.displayAsImages"
            class="mt-4 grid grid-cols-2 justify-start gap-8 sm:grid-cols-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-12 2xl:grid-cols-4 2xl:gap-x-16"
        >
            <ConfiguratorButtonImage
                v-for="choice in choicesWithoutHiddenElements"
                :key="choice.value"
                :label="choice.label"
                :image-url="choice.imageUrl as string"
                :selected="selections[control.key] === choice.value"
                :disabled="choice.disabled"
                :clickable-when-disabled="true"
                :errored="showErrors && !!control.errorMessage"
                :tooltip="choice.tooltip"
                :data-cy="
                    slugify(`configurator-${control.key}-${choice.value}`)
                "
                width-class="w-full"
                @click="onButtonClick(control.key, choice.value)"
            />

            <slot name="after-choices" />
        </div>

        <div v-else class="mt-4">
            <Dropdown
                v-if="
                    choicesWithoutHiddenElements.length > 4 &&
                    'material' !== control.key
                "
                class="appearance-blocky"
                :choices="choicesWithoutHiddenElements"
                :model-value="
                    control.key in selections ? selections[control.key] : null
                "
                :is-errored="showErrors && !!control.errorMessage"
                :data-cy="`configurator-${control.key}`"
                @update:model-value="onButtonClick(control.key, $event)"
            />

            <div
                v-else
                class="grid gap-3 sm:grid-cols-2 md:grid-cols-1"
                :class="hasLongButton ? 'xl:grid-cols-2' : 'lg:grid-cols-2'"
            >
                <ConfiguratorButtonText
                    v-for="choice in choicesWithoutHiddenElements"
                    :key="choice.value"
                    :label="choice.label"
                    :selected="selections[control.key] === choice.value"
                    class="w-full sm:flex-1"
                    :disabled="choice.disabled"
                    :errored="
                        showErrors && !!control.errorMessage && !choice.disabled
                    "
                    :tooltip="choice.tooltip"
                    :data-cy="
                        slugify(`configurator-${control.key}-${choice.value}`)
                    "
                    :data-cy-unavailable="choice.disabled"
                    @click="onButtonClick(control.key, choice.value)"
                />
            </div>
        </div>

        <div
            v-if="showErrors && control.errorMessage"
            class="mt-2 text-sm text-error"
        >
            {{ control.errorMessage }}
        </div>

        <slot name="after-control" />

        <template v-if="control.modalComponent">
            <component :is="control.modalComponent" ref="detailsModal" />
        </template>
        <ConfiguratorControlModal
            v-else-if="hasDetailsModal"
            ref="detailsModal"
            :control="control"
        />
    </div>
</template>

<script lang="ts">
import {
    BagLabelSizeModal,
    BeverageLabelSizeModal,
    StickerSizeModal,
} from '~/components/quote/components/modals';

export default {
    components: {
        BeverageLabelSizeModal: BeverageLabelSizeModal,
        BagLabelSizeModal: BagLabelSizeModal,
        StickerSizeModal: StickerSizeModal,
    },
};
</script>

<script setup lang="ts">
import ConfiguratorButtonImage from '~/components/configurator/ConfiguratorButtonImage.vue';
import ConfiguratorButtonText from '~/components/configurator/ConfiguratorButtonText.vue';
import ConfiguratorControlModal from '~/components/configurator/ConfiguratorControlModal.vue';
import {
    ConfiguratorControl,
    ConfiguratorSelections,
} from '~/types/configurator';

const props = defineProps<{
    control: ConfiguratorControl;
    selections: ConfiguratorSelections;
    showErrors: boolean;
    onButtonClick: (controlKey: string, value: number | string) => void;
}>();

const hasDetailsModal = computed(() => {
    return (
        !!props.control.choices.find((choice) => !!choice.description) ||
        !!props.control.modalComponent
    );
});

const isVisible = computed(() => !props.control.hideOnProductPage);

const hasLongButton = computed(() => {
    const longButtons = props.control.choices.filter(
        (choice) => choice.label.length >= 18,
    );

    return longButtons.length > 0;
});

const choicesWithoutHiddenElements = computed(() => {
    return props.control.choices.filter(
        (choice) => choice?.hideFromSelections !== true,
    );
});
</script>
